/**
 * Product Service - Firestore operations for product management
 *
 * Products are specific items from a brand's website that can be
 * featured in creatives. URLs are scanned to extract product details.
 */

import {
    collection,
    doc,
    addDoc,
    getDoc,
    getDocs,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    serverTimestamp
} from 'firebase/firestore';
import { db, storage } from '../lib/firebase';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { GoogleGenerativeAI } from '@google/generative-ai';

const PRODUCTS_COLLECTION = 'products';

/**
 * Extract a normalized filename from an image URL for duplicate detection.
 * Strips query parameters, CDN size suffixes (e.g., _800x600, _grande, _large),
 * and normalizes the path so the same image at different sizes is recognized as one.
 * @param {string} url - The image URL
 * @returns {string} Normalized filename key
 */
function getNormalizedImageFilename(url) {
    try {
        const parsed = new URL(url);
        // Get the pathname, decode it
        let pathname = decodeURIComponent(parsed.pathname);

        // Extract just the filename from the path
        const segments = pathname.split('/');
        let filename = segments[segments.length - 1] || '';

        // Remove common CDN size suffixes before the extension
        // e.g., image_800x600.jpg -> image.jpg, image_grande.jpg -> image.jpg
        filename = filename
            .replace(/_\d+x\d*/g, '')           // _800x600, _800x
            .replace(/_\d+x/g, '')              // _800x
            .replace(/x\d+\./g, '.')            // x600.
            .replace(/_(?:grande|large|medium|small|compact|thumb|master|pico|icon|original)\./gi, '.')
            .replace(/@\d+x\./g, '.')           // @2x. retina suffixes
            .replace(/\?.*$/, '');              // strip any remaining query string

        // Lowercase for case-insensitive comparison
        return filename.toLowerCase();
    } catch {
        // If URL parsing fails, return the original string lowercased
        return url.toLowerCase();
    }
}

/**
 * Deduplicate images by normalized filename.
 * Keeps the first occurrence when multiple URLs resolve to the same filename.
 * @param {string[]} imageUrls - Array of image URL strings
 * @returns {string[]} Deduplicated array
 */
function deduplicateImageUrls(imageUrls) {
    const seen = new Map(); // normalizedFilename -> original URL
    const result = [];

    for (const url of imageUrls) {
        const key = getNormalizedImageFilename(url);
        if (!key || seen.has(key)) continue;
        seen.set(key, url);
        result.push(url);
    }

    return result;
}

/**
 * Deduplicate image objects (with url/alt) by normalized filename.
 * Keeps the first occurrence when multiple entries resolve to the same filename.
 * @param {Array<{url: string, alt?: string}>} images - Array of image objects
 * @returns {Array<{url: string, alt?: string}>} Deduplicated array
 */
export function deduplicateImageObjects(images) {
    const seen = new Map();
    const result = [];

    for (const img of images) {
        const url = typeof img === 'string' ? img : img.url;
        if (!url) continue;
        const key = getNormalizedImageFilename(url);
        if (!key || seen.has(key)) continue;
        seen.set(key, true);
        result.push(img);
    }

    return result;
}

// Initialize Gemini for product extraction
const genAI = new GoogleGenerativeAI(import.meta.env.VITE_GEMINI_API_KEY);
const GEMINI_MODEL = import.meta.env.VITE_GEMINI_MODEL || 'gemini-2.5-flash';

/**
 * Default product structure
 */
export const DEFAULT_PRODUCT = {
    name: '',
    description: '',
    visualNotes: '',  // AI guidance for visual appearance in generated images
    price: '',
    currency: 'USD',
    imageUrl: '',     // Primary/thumbnail image (for backward compatibility)
    images: [],       // Array of all product images: [{ url: string, alt?: string }]
    productUrl: '',
    category: '',
    features: [],
    isActive: true
};

/**
 * Upload a product image to Firebase Storage
 * @param {string} imageDataUrl - Base64 data URL of the image
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID
 * @returns {Promise<string>} - The download URL
 */
export async function uploadProductImage(imageDataUrl, userId, brandId) {
    const mimeMatch = imageDataUrl.match(/data:image\/([^;]+);/);
    const extension = mimeMatch ? mimeMatch[1].replace('jpeg', 'jpg') : 'png';

    const imageId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const storageRef = ref(storage, `product-images/${userId}/${brandId}/${imageId}.${extension}`);

    await uploadString(storageRef, imageDataUrl, 'data_url');
    const downloadURL = await getDownloadURL(storageRef);

    return downloadURL;
}

/**
 * Create a new product
 */
export async function createProduct(brandId, userId, productData) {
    if (!brandId || !userId) {
        throw new Error('Brand ID and User ID are required');
    }

    const product = {
        ...DEFAULT_PRODUCT,
        ...productData,
        brandId,
        userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, PRODUCTS_COLLECTION), product);

    return {
        id: docRef.id,
        ...product
    };
}

/**
 * Get a single product by ID
 */
export async function getProduct(productId) {
    const docRef = doc(db, PRODUCTS_COLLECTION, productId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
        return null;
    }

    return {
        id: docSnap.id,
        ...docSnap.data()
    };
}

/**
 * Get all products for a brand
 */
export async function getProductsByBrand(brandId) {
    if (!brandId) {
        throw new Error('Brand ID is required');
    }

    const q = query(
        collection(db, PRODUCTS_COLLECTION),
        where('brandId', '==', brandId),
        orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
    }));
}

/**
 * Get active products for a brand
 */
export async function getActiveProducts(brandId) {
    const products = await getProductsByBrand(brandId);
    return products.filter(p => p.isActive);
}

/**
 * Update a product
 */
export async function updateProduct(productId, updates) {
    const docRef = doc(db, PRODUCTS_COLLECTION, productId);

    await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
    });

    return getProduct(productId);
}

/**
 * Delete a product
 */
export async function deleteProduct(productId) {
    const docRef = doc(db, PRODUCTS_COLLECTION, productId);
    await deleteDoc(docRef);
}

/**
 * Scan a URL and extract product information using AI
 * @param {string} url - The product URL to scan
 * @returns {Promise<Object>} Extracted product data
 */
export async function scanProductUrl(url) {
    if (!url) {
        throw new Error('URL is required');
    }

    try {
        // Fetch the page content
        const response = await fetch(`/api/fetch-url?url=${encodeURIComponent(url)}`);

        if (!response.ok) {
            // Try direct fetch as fallback (may hit CORS issues)
            const directResponse = await fetch(url);
            if (!directResponse.ok) {
                throw new Error('Failed to fetch URL');
            }
            const html = await directResponse.text();
            return extractProductFromHtml(html, url);
        }

        const data = await response.json();
        return extractProductFromHtml(data.html, url);
    } catch (error) {
        console.error('Error fetching URL:', error);
        // If fetch fails, use AI to help with just the URL
        return extractProductFromUrl(url);
    }
}

/**
 * Pre-extract image URLs and JSON-LD structured data from the full HTML
 * before truncation, so we don't lose images that appear later in the page.
 */
function preExtractFromHtml(html, url) {
    const baseUrl = new URL(url);

    const resolveUrl = (src) => {
        if (!src) return null;
        src = src.trim();
        if (src.startsWith('data:') || src.startsWith('javascript:')) return null;
        try {
            if (src.startsWith('http')) return src;
            return new URL(src, baseUrl.origin).href;
        } catch {
            return null;
        }
    };

    // Extract JSON-LD structured data
    let jsonLdData = null;
    const jsonLdPattern = /<script[^>]*type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/gi;
    let jsonLdMatch;
    while ((jsonLdMatch = jsonLdPattern.exec(html)) !== null) {
        try {
            const parsed = JSON.parse(jsonLdMatch[1]);
            // Handle both single objects and arrays
            const items = Array.isArray(parsed) ? parsed : [parsed];
            for (const item of items) {
                if (item['@type'] === 'Product' || item['@type']?.includes?.('Product')) {
                    jsonLdData = item;
                    break;
                }
                // Check @graph array (common in some CMS)
                if (item['@graph']) {
                    const product = item['@graph'].find(g =>
                        g['@type'] === 'Product' || g['@type']?.includes?.('Product')
                    );
                    if (product) {
                        jsonLdData = product;
                        break;
                    }
                }
            }
        } catch {
            // Invalid JSON-LD, skip
        }
        if (jsonLdData) break;
    }

    // Extract JSON-LD images
    const jsonLdImages = [];
    if (jsonLdData?.image) {
        const imgs = Array.isArray(jsonLdData.image) ? jsonLdData.image : [jsonLdData.image];
        for (const img of imgs) {
            const imgUrl = typeof img === 'string' ? img : img?.url;
            const resolved = resolveUrl(imgUrl);
            if (resolved) jsonLdImages.push(resolved);
        }
    }

    // Extract all image URLs from the full HTML using regex
    const imageUrls = new Set();
    const imgPatterns = [
        // Standard src attributes
        /<img[^>]+src=["']([^"']+)["'][^>]*/gi,
        // data-src (lazy loading)
        /<img[^>]+data-src=["']([^"']+)["'][^>]*/gi,
        // data-zoom-image
        /data-zoom-image=["']([^"']+)["']/gi,
        // data-large
        /data-large=["']([^"']+)["']/gi,
        // data-full
        /data-full=["']([^"']+)["']/gi,
        // srcset (take first entry)
        /srcset=["']([^\s"']+)/gi,
        // Shopify CDN patterns in JS/JSON
        /["'](\/\/cdn\.shopify\.com\/s\/files\/[^"']+\.(?:jpg|jpeg|png|webp)[^"']*)["']/gi,
        // General CDN image patterns in JS/JSON
        /["'](https?:\/\/[^"']+\.(?:jpg|jpeg|png|webp)(?:\?[^"']*)?)["']/gi,
    ];

    for (const pattern of imgPatterns) {
        let match;
        while ((match = pattern.exec(html)) !== null) {
            const resolved = resolveUrl(match[1]);
            if (resolved) imageUrls.add(resolved);
        }
    }

    // Filter out likely non-product images
    const filteredImages = Array.from(imageUrls).filter(imgUrl => {
        const lower = imgUrl.toLowerCase();
        // Skip tiny icons, tracking pixels, logos, social icons
        if (lower.includes('icon') && !lower.includes('icon-')) return false;
        if (lower.includes('logo')) return false;
        if (lower.includes('favicon')) return false;
        if (lower.includes('pixel')) return false;
        if (lower.includes('tracking')) return false;
        if (lower.includes('badge')) return false;
        if (lower.includes('payment')) return false;
        if (lower.includes('social')) return false;
        if (lower.includes('avatar')) return false;
        if (lower.match(/\d+x\d+/) && lower.match(/(\d+)x(\d+)/)) {
            const dims = lower.match(/(\d+)x(\d+)/);
            if (parseInt(dims[1]) < 50 || parseInt(dims[2]) < 50) return false;
        }
        // Skip SVGs (usually icons/logos)
        if (lower.endsWith('.svg')) return false;
        // Skip GIFs (usually animations/banners)
        if (lower.endsWith('.gif')) return false;
        return true;
    });

    // Deduplicate by normalized filename (same image at different sizes)
    const dedupedImages = deduplicateImageUrls(filteredImages);

    return {
        jsonLdData,
        jsonLdImages,
        allImageUrls: dedupedImages.slice(0, 50)
    };
}

/**
 * Extract product data from HTML using AI
 */
async function extractProductFromHtml(html, url) {
    const model = genAI.getGenerativeModel({ model: GEMINI_MODEL });

    // Pre-extract images and structured data from the FULL HTML before truncating
    const preExtracted = preExtractFromHtml(html, url);

    // Truncate HTML for the AI prompt
    const truncatedHtml = html.substring(0, 30000);

    // Build structured data section if we found JSON-LD
    let structuredDataSection = '';
    if (preExtracted.jsonLdData) {
        structuredDataSection = `\n\nJSON-LD Structured Data found on page:\n${JSON.stringify(preExtracted.jsonLdData, null, 2).substring(0, 5000)}`;
    }

    // Build pre-extracted images section
    let preExtractedImagesSection = '';
    if (preExtracted.allImageUrls.length > 0) {
        preExtractedImagesSection = `\n\nAll image URLs found on page (${preExtracted.allImageUrls.length} total):\n${preExtracted.allImageUrls.slice(0, 30).join('\n')}`;
    }

    const prompt = `Extract product information from this e-commerce page HTML.

URL: ${url}
${structuredDataSection}
${preExtractedImagesSection}

HTML Content (truncated):
${truncatedHtml}

Extract and return a JSON object with:
{
  "name": "Product name",
  "description": "Product description (1-2 sentences)",
  "visualNotes": "Physical appearance details for AI image generation - describe the product's colors, shape, size, materials, packaging, logo placement, and any distinctive visual features",
  "price": "Price as shown (include currency symbol if present)",
  "currency": "USD/EUR/GBP/etc",
  "imageUrl": "Main product image URL (full URL) - the primary/hero image",
  "images": [
    { "url": "full URL to product image 1", "alt": "image description if available" },
    { "url": "full URL to product image 2", "alt": "image description if available" }
  ],
  "category": "Product category",
  "features": ["feature1", "feature2", "feature3"]
}

IMPORTANT for images:
- Use the JSON-LD data and the pre-extracted image URL list above (if provided) to identify ALL product images
- The "imageUrl" field should contain the main/hero product image
- The "images" array should contain ALL OTHER product images (NOT the main one - it's already in imageUrl)
- Include images from different angles, details, packaging, lifestyle shots
- Convert relative URLs to absolute URLs using the base domain
- Include up to 10 additional images maximum in the images array
- Filter out non-product images (icons, logos, social media icons, navigation elements, banners)
- Do NOT duplicate the main imageUrl in the images array

If you can't find a field, use an empty string or empty array. Return ONLY the JSON object.`;

    const result = await model.generateContent(prompt);
    const response = await result.response;
    const text = response.text();

    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
        throw new Error('Failed to extract product information');
    }

    const extracted = JSON.parse(jsonMatch[0]);

    // Ensure images array is properly formatted
    let images = extracted.images || [];
    if (!Array.isArray(images)) {
        images = [];
    }
    // Normalize image objects (ensure they have url property)
    images = images.map(img => {
        if (typeof img === 'string') {
            return { url: img, alt: '' };
        }
        return { url: img.url || '', alt: img.alt || '' };
    }).filter(img => img.url);

    // If imageUrl exists but not in images array, add it as first
    if (extracted.imageUrl && !images.some(img => img.url === extracted.imageUrl)) {
        images.unshift({ url: extracted.imageUrl, alt: 'Primary product image' });
    }

    // Deduplicate images by normalized filename (same image at different sizes/resolutions)
    images = deduplicateImageObjects(images);

    return {
        ...DEFAULT_PRODUCT,
        ...extracted,
        images,
        productUrl: url
    };
}

/**
 * Extract product data from just the URL using AI
 * (Used as fallback when page fetch fails)
 */
async function extractProductFromUrl(url) {
    const model = genAI.getGenerativeModel({ model: GEMINI_MODEL });

    const prompt = `Based on this product URL, make educated guesses about the product:

URL: ${url}

Look at the URL structure, path segments, and any product identifiers to infer:
{
  "name": "Best guess at product name from URL",
  "description": "Brief description based on URL context",
  "visualNotes": "",
  "price": "",
  "currency": "USD",
  "imageUrl": "",
  "category": "Category inferred from URL",
  "features": []
}

Return ONLY the JSON object. Use empty strings for fields you can't determine.`;

    const result = await model.generateContent(prompt);
    const response = await result.response;
    const text = response.text();

    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
        return {
            ...DEFAULT_PRODUCT,
            productUrl: url,
            name: 'Product from ' + new URL(url).hostname
        };
    }

    const extracted = JSON.parse(jsonMatch[0]);

    return {
        ...DEFAULT_PRODUCT,
        ...extracted,
        productUrl: url
    };
}

/**
 * Parse a data: URL into { mimeType, data } for sending to the server.
 */
function parseDataUrl(dataUrl) {
    const match = dataUrl.match(/^data:(image\/[^;]+);base64,(.+)$/s);
    if (!match) return null;
    return { mimeType: match[1], data: match[2] };
}

/**
 * Generate visual notes and product description from product images using AI
 * Routes through the server to avoid CSP issues with external image URLs
 * @param {string} imageUrl - The primary product image URL
 * @param {string} productName - Product name for context
 * @param {Array} additionalImages - Additional product images [{url, alt}]
 * @returns {Promise<{visualNotes: string, description: string}>} Generated visual notes and description
 */
export async function generateVisualNotes(imageUrl, productName = '', additionalImages = []) {
    const imageUrls = [];
    const base64Images = [];
    const seen = new Set();

    // Collect all unique images from both imageUrl and additionalImages
    const allSources = [];
    if (imageUrl) allSources.push(imageUrl);
    for (const img of additionalImages) {
        const url = typeof img === 'string' ? img : img.url;
        if (url) allSources.push(url);
    }

    for (const url of allSources) {
        if (seen.has(url)) continue;
        seen.add(url);

        if (url.startsWith('data:')) {
            const parsed = parseDataUrl(url);
            if (parsed) base64Images.push(parsed);
        } else {
            imageUrls.push(url);
        }
    }

    console.log(`[generateVisualNotes] Sending ${imageUrls.length} URL(s) + ${base64Images.length} base64 image(s) to server`);

    if (imageUrls.length === 0 && base64Images.length === 0) {
        throw new Error('At least one image is required');
    }

    const response = await fetch('http://localhost:3001/api/generate-visual-notes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            imageUrl: imageUrls[0] || null,
            imageUrls,
            base64Images,
            productName
        })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.error || 'Failed to generate visual notes');
    }

    const data = await response.json();
    return { visualNotes: data.visualNotes || '', description: data.description || '' };
}

/**
 * Scan website for product URLs
 * @param {string} websiteUrl - The main website URL
 * @returns {Promise<Array>} Array of discovered product URLs
 */
export async function discoverProductUrls(websiteUrl) {
    try {
        console.log('Discovering products from:', websiteUrl);
        const response = await fetch(`/api/fetch-url?url=${encodeURIComponent(websiteUrl)}`);

        if (!response.ok) {
            throw new Error('Failed to fetch website');
        }

        const data = await response.json();
        const html = data.html;

        console.log('Fetched HTML length:', html.length);

        // First, try to extract product URLs using regex patterns (faster, more reliable)
        const baseUrl = new URL(websiteUrl);
        const productUrls = new Set();

        // Common e-commerce product URL patterns
        const productPatterns = [
            // Shopify: /products/product-name
            /href=["']([^"']*\/products\/[^"'?#]+)/gi,
            // Generic: /product/id or /product/name
            /href=["']([^"']*\/product\/[^"'?#]+)/gi,
            // WooCommerce: /product/name
            /href=["']([^"']*\/shop\/[^"']*\/[^"'?#]+)/gi,
            // BigCommerce and others
            /href=["']([^"']*\/p\/[^"'?#]+)/gi,
            // Item pattern
            /href=["']([^"']*\/item\/[^"'?#]+)/gi,
        ];

        for (const pattern of productPatterns) {
            let match;
            while ((match = pattern.exec(html)) !== null) {
                let url = match[1];
                // Skip if it's a collection/category URL
                if (url.includes('/collections/') && !url.includes('/products/')) continue;
                if (url.includes('/categories/')) continue;
                if (url.includes('/category/')) continue;

                // Make absolute URL
                try {
                    if (url.startsWith('/')) {
                        url = baseUrl.origin + url;
                    } else if (!url.startsWith('http')) {
                        url = new URL(url, baseUrl.origin).href;
                    }
                    productUrls.add(url);
                } catch (e) {
                    // Invalid URL, skip
                }
            }
        }

        console.log('Found product URLs via regex:', productUrls.size);

        // If regex found products, return them
        if (productUrls.size > 0) {
            return Array.from(productUrls).slice(0, 50); // Limit to 50 products
        }

        // Fallback: Use AI to find product URLs from the page
        console.log('Falling back to AI extraction...');
        const model = genAI.getGenerativeModel({ model: GEMINI_MODEL });

        const prompt = `Extract all individual PRODUCT page URLs from this e-commerce website HTML.

Website: ${websiteUrl}
HTML (truncated):
${html.substring(0, 30000)}

IMPORTANT:
- Find links to INDIVIDUAL product pages (NOT category/collection pages)
- For Shopify stores, look for /products/product-name URLs
- For other stores, look for /product/, /p/, /item/, /shop/*/product patterns
- Extract the full URL path from href attributes
- Include product links from product grids, cards, or listings

Return ONLY a JSON array of unique product URLs (use absolute URLs with the domain ${baseUrl.origin}):
["${baseUrl.origin}/products/example-1", "${baseUrl.origin}/products/example-2"]

If no product URLs found, return an empty array: []
Return ONLY the JSON array, no other text.`;

        const result = await model.generateContent(prompt);
        const response2 = await result.response;
        const text = response2.text();

        console.log('AI response:', text.substring(0, 200));

        const jsonMatch = text.match(/\[[\s\S]*?\]/);
        if (!jsonMatch) {
            console.log('No JSON array found in AI response');
            return [];
        }

        const urls = JSON.parse(jsonMatch[0]);
        console.log('AI found URLs:', urls.length);

        // Ensure URLs are absolute and valid
        return urls.map(url => {
            try {
                if (url.startsWith('http')) {
                    return url;
                }
                return new URL(url, baseUrl.origin).href;
            } catch {
                return null;
            }
        }).filter(Boolean).slice(0, 50);
    } catch (error) {
        console.error('Error discovering product URLs:', error);
        throw error; // Re-throw to show error in UI
    }
}

/**
 * Build product context for AI prompts
 * @param {Array} products - Array of products
 * @param {Object} options - Configuration options
 * @param {number} options.maxProducts - Maximum number of products to include (default: 3, use 0 for all)
 * @param {Array} options.productIds - Specific product IDs to include (overrides random selection)
 */
export function buildProductContext(products, options = {}) {
    if (!products || products.length === 0) {
        return '';
    }

    const { maxProducts = 3, productIds = null } = options;

    let activeProducts = products.filter(p => p.isActive);
    if (activeProducts.length === 0) {
        return '';
    }

    // If specific product IDs are provided, filter to those
    if (productIds && productIds.length > 0) {
        activeProducts = activeProducts.filter(p => productIds.includes(p.id));
    }
    // Otherwise, randomly select up to maxProducts (if maxProducts > 0)
    else if (maxProducts > 0 && activeProducts.length > maxProducts) {
        // Shuffle and take first N products
        const shuffled = [...activeProducts].sort(() => Math.random() - 0.5);
        activeProducts = shuffled.slice(0, maxProducts);
    }

    if (activeProducts.length === 0) {
        return '';
    }

    const totalCount = products.filter(p => p.isActive).length;
    const showingSubset = activeProducts.length < totalCount;

    return `
## SPECIFIC PRODUCTS TO FEATURE${showingSubset ? ` (${activeProducts.length} of ${totalCount} products)` : ''}
The following are real products from the brand. Use ONLY these products in creatives - do not invent or guess product names, prices, or features.

${activeProducts.map((p, i) => {
        // Get all image URLs (from images array or fallback to imageUrl)
        const allImages = p.images?.length > 0
            ? p.images.map(img => typeof img === 'string' ? img : img.url).filter(Boolean)
            : (p.imageUrl ? [p.imageUrl] : []);

        return `
### Product ${i + 1}: ${p.name}
${p.description ? `Description: ${p.description}` : ''}
${p.visualNotes ? `Visual Appearance: ${p.visualNotes}` : ''}
${p.price ? `Price: ${p.price}` : ''}
${p.category ? `Category: ${p.category}` : ''}
${p.features?.length > 0 ? `Features: ${p.features.join(', ')}` : ''}
${p.productUrl ? `URL: ${p.productUrl}` : ''}
${allImages.length > 0 ? `Product Images (${allImages.length} total): ${allImages[0]}${allImages.length > 1 ? ` (+${allImages.length - 1} more angles)` : ''}` : ''}
`;
    }).join('\n')}

IMPORTANT: Only reference the products listed above. Do not make up product names, prices, or features.
`;
}
